import {
  ExchangeEstimate,
  TradeEstimates,
  SwapMarket,
  SwapProContext,
} from '../../../pages/SwapPage';
import { TokenMetadata } from '../../../services/ft-contract';
import React, { useContext } from 'react';
import Big from 'big.js';
import { FormattedMessage } from 'react-intl';
import { isMobile } from '../../../utils/device';
import { toRealSymbol } from '../../../utils/token';
import {
  ONLY_ZEROS,
  scientificNotationToString,
  numberWithCommas,
} from '../../../utils/numbers';
import { getDexIcon, getDexAction } from './routeHelpers';
import { displayNumberToAppropriateDecimals } from '../../../services/dcl/dclDisplay';

export const MarketList = ({
  trade,
  allTrades,
  selectMarket,
  tokenIn,
  tokenOut,
}: {
  trade: ExchangeEstimate;
  allTrades: TradeEstimates;
  selectMarket: SwapMarket;
  tokenIn: TokenMetadata;
  tokenOut: TokenMetadata;
}) => {
  const { setSelectMarket } = useContext(SwapProContext);

  const sortedTradesList = Object.values(allTrades)
    .filter((tradeEntry) => !!tradeEntry.availableRoute)
    .sort((tradeA, tradeB) => {
      return new Big(tradeA.tokenOutAmount || '0').gt(
        new Big(tradeB.tokenOutAmount || '0')
      )
        ? -1
        : 1;
    });

  const bestAmount = sortedTradesList?.[0]?.tokenOutAmount;

  if (!bestAmount || tokenIn?.id === tokenOut?.id)
    return (
      <>
        <div className="pb-4 max-w-max mt-6 flex flex-col">
          <span className="pb-1.5 text-white">
            <FormattedMessage
              id="markets"
              defaultMessage={'Markets'}
            ></FormattedMessage>
          </span>
        </div>
        {!isMobile() && (
          <table
            className="w-full table relative right-3 border-separate"
            style={{
              borderSpacing: 0,
            }}
          >
            <tr
              className="text-primaryText"
              style={{
                fontSize: '13px',
              }}
            >
              <th align="left" className="pb-2 pl-3">
                <FormattedMessage id="exchanges" defaultMessage={'Exchanges'} />
              </th>

              <th align="left">
                {<FormattedMessage id="price" defaultMessage={'Price'} />}
                <span className="ml-1">
                  {`(${toRealSymbol(trade.tokenOut.symbol)}/${toRealSymbol(
                    trade.tokenIn.symbol
                  )})`}
                </span>
              </th>

              <th align="left">
                <FormattedMessage
                  id="output_est"
                  defaultMessage={'Output (est.)'}
                />
              </th>

              <th align="left">
                <FormattedMessage id="diff" defaultMessage={'Diff'} />
              </th>

              <th align="left"></th>
            </tr>
          </table>
        )}

        <div
          className="text-center mt-10 text-sm"
          style={{
            color: '#566069',
          }}
        >
          <FormattedMessage
            id="no_trade_routes"
            defaultMessage={'No trade routes.'}
          />
        </div>
      </>
    );

  const displayList = sortedTradesList.map((tradeEntry) => {
    const rawRate = scientificNotationToString(
      new Big(tradeEntry.tokenOutAmount || '0')
        .div(
          ONLY_ZEROS.test(tradeEntry.tokenInAmount || '0')
            ? '1'
            : tradeEntry.tokenInAmount
        )
        .toString()
    );
    return {
      ...tradeEntry,
      marketIcon: getDexIcon(tradeEntry.market),
      diff:
        tradeEntry.tokenOutAmount === bestAmount
          ? 'best'
          : new Big(tradeEntry.tokenOutAmount || '0')
              .div(bestAmount || '1')
              .minus(1)
              .times(100)
              .toFixed(2),
      rate: numberWithCommas(displayNumberToAppropriateDecimals(rawRate)),
      selected: selectMarket === tradeEntry.market,
      output: numberWithCommas(
        displayNumberToAppropriateDecimals(tradeEntry.tokenOutAmount)
      ),
      action: getDexAction(tradeEntry.market),
    };
  });

  return (
    <>
      <div className="pb-4 max-w-max mt-6 flex flex-col">
        <span className="pb-1.5 text-white">
          <FormattedMessage
            id="markets"
            defaultMessage={'Markets'}
          ></FormattedMessage>
        </span>

        <div
          className="w-full xsm:hidden rounded-md bg-senderHot"
          style={{
            height: '3px',
          }}
        ></div>
      </div>

      {!isMobile() && (
        <table
          className="w-full table relative right-3 border-separate"
          style={{
            borderSpacing: 0,
          }}
        >
          <tr
            className="text-primaryText"
            style={{
              fontSize: '13px',
            }}
          >
            <th align="left" className="pb-2 pl-3">
              <FormattedMessage id="exchanges" defaultMessage={'Exchanges'} />
            </th>

            <th align="left">
              {<FormattedMessage id="price" defaultMessage={'Price'} />}
              <span className="ml-1">
                {`(${toRealSymbol(trade.tokenOut.symbol)}/${toRealSymbol(
                  trade.tokenIn.symbol
                )})`}
              </span>
            </th>

            <th align="left">
              <FormattedMessage
                id="output_est"
                defaultMessage={'Output (est.)'}
              />
            </th>

            <th align="left">
              <FormattedMessage id="diff" defaultMessage={'Diff'} />
            </th>

            <th align="left"></th>
          </tr>
          {displayList.map((tradeEntry) => {
            return (
              <>
                <tr
                  className={`text-white rounded-xl text-sm p-2 
              hover:bg-inputV3BorderColor hover:bg-opacity-40
              ${
                tradeEntry.selected ? 'bg-inputV3BorderColor bg-opacity-40' : ''
              }
            
            `}
                  onClick={() => {
                    setSelectMarket(tradeEntry.market);
                  }}
                >
                  <td className="rounded-l-xl">
                    <div className="frcs pl-3">
                      <div
                        className="frcc "
                        style={{
                          height: '25px',
                          width: '25px',
                        }}
                      >
                        {tradeEntry.marketIcon}
                      </div>

                      <div className="ml-2 frcc">
                        {tradeEntry?.exchange_name}
                      </div>
                    </div>
                  </td>

                  <td>{tradeEntry.rate}</td>

                  <td>{tradeEntry.output}</td>

                  <td>
                    {tradeEntry.diff === 'best' ? (
                      <span className="text-black text-xs my-2 max-w-max rounded-2xl bg-senderHot px-2.5 py-0.5 frcc ">
                        <FormattedMessage
                          id="best"
                          defaultMessage={'Best'}
                        ></FormattedMessage>
                      </span>
                    ) : (
                      <span className="bg-sellRed my-2  text-xs max-w-max bg-opacity-10 text-sellRed rounded-2xl px-2.5 py-0.5 frcc">
                        {tradeEntry.diff}%
                      </span>
                    )}
                  </td>

                  <td className="rounded-r-xl">
                    <div
                      className="w-4  h-4 rounded-full  frcc"
                      style={{
                        border: '1px solid #223846',
                      }}
                    >
                      {tradeEntry.selected && (
                        <div
                          className="rounded-full bg-senderHot"
                          style={{
                            height: '9px',
                            width: '9px',
                          }}
                        ></div>
                      )}
                    </div>
                  </td>
                </tr>

                <tr>
                  <td>
                    <div className="my-1"></div>
                  </td>
                </tr>
              </>
            );
          })}
        </table>
      )}

      {isMobile() &&
        displayList.map((tradeEntry, index) => {
          return (
            <div
              className={`${
                tradeEntry.selected ? 'border border-gradientFromHover' : ''
              } bg-menuMoreBgColor rounded-xl
            
              p-2.5 mb-3
            `}
              onClick={() => {
                setSelectMarket(tradeEntry.market);
              }}
            >
              <div className="frcb ">
                <div className="frcs">
                  <div
                    className="frcc "
                    style={{
                      height: '25px',
                      width: '25px',
                    }}
                  >
                    {tradeEntry.marketIcon}
                  </div>

                  <div className="mx-2 frcc">{tradeEntry?.exchange_name}</div>

                  <div>
                    {tradeEntry.diff === 'best' ? (
                      <span className="text-black text-xs my-2 max-w-max rounded-2xl bg-senderHot px-2.5 py-0.5 frcc ">
                        <FormattedMessage
                          id="best"
                          defaultMessage={'Best'}
                        ></FormattedMessage>
                      </span>
                    ) : (
                      <span className="bg-sellRed my-2  text-xs max-w-max bg-opacity-10 text-sellRed rounded-2xl px-2.5 py-0.5 frcc">
                        {tradeEntry.diff}%
                      </span>
                    )}
                  </div>
                </div>

                <div
                  className="w-4  h-4 rounded-full  frcc"
                  style={{
                    border: '1px solid #223846',
                  }}
                >
                  {tradeEntry.selected && (
                    <div
                      className="rounded-full bg-senderHot"
                      style={{
                        height: '9px',
                        width: '9px',
                      }}
                    ></div>
                  )}
                </div>
              </div>

              <div className="frcb py-3 text-13px">
                <div className=" text-primaryText">
                  {<FormattedMessage id="price" defaultMessage={'Price'} />}
                  <span className="ml-1">
                    {`(${toRealSymbol(trade.tokenOut.symbol)}/${toRealSymbol(
                      trade.tokenIn.symbol
                    )})`}
                  </span>
                </div>

                <div className="text-white">{tradeEntry.rate}</div>
              </div>

              <div className="frcb text-13px">
                <div className=" text-primaryText">
                  <FormattedMessage
                    id="output_est"
                    defaultMessage={'Output (est.)'}
                  />
                </div>

                <div className="text-white">{tradeEntry.output}</div>
              </div>
            </div>
          );
        })}
    </>
  );
};
